import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, DropdownMenu, TableItem } from 'components/atoms';
import { FormInput, LabeledStatus, MagellanPopups, TableDropdown } from 'components/molecules';
import LabeledStatementConsistancy from 'components/molecules/labeledStatus/LabeledStatementConsistancy';
import Popup from 'components/molecules/popup';
import { FooterTable, QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { Formik } from 'formik';
import { useComponentVisible, useHasPermission } from 'hooks';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useTranslation } from 'react-i18next';
import {
  PiDownloadBold,
  PiFileCsvDuotone,
  PiMicrosoftExcelLogo,
  PiTrash,
} from 'react-icons/pi';
import { Link, useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useGetProvidersInfiniteList } from 'services/providers';
import { useGetSalesChannelsLightList } from 'services/salesChannel';
import { Provider } from 'services/salesChannel/types';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import {
  useDeleteStatement,
  useGetStatementListInfinite,
  useGetStatementStatistics,
  usePOSTChunkedUpload,
} from 'services/statements';
import {
  CURRENCY_ID,
  IStatement,
  IStatementImportPayload,
  IStatementsSearch,
} from 'services/statements/types';
import { useSearchById, useSetSearchById } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { magellanColumns } from 'types/components/table/table.types';
import { booleanSelectOptions } from 'utils/components/inputs/select';
import { formatCurrency } from 'utils/global/currency.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { getNodes } from 'utils/table/nodes.utils';

enum SORT_COLUMNS {
  settlementPeriod,
  salesChannel,
  provider,
  totalValue,
  detailedConsistency,
  generalConsistency,
  hasUnrelatedRows,
  status,
}

const statementsSearchInitialValues: IStatementsSearch = {
  startSettlementPeriodId: '',
  endSettlementPeriodId: '',
  salesChannels: '',
  providers: '',
  hasUnrelatedRows: undefined,
};

const Statements = () => {
  usePermissionGuard('StatementPermissionsLIST**', '/', true, 'statements');
  const hasPermission = useHasPermission();

  const { data: providers } = useGetProvidersInfiniteList({ itemCount: 999 });

  const settlementPeriods = useGetSettlementLightList();
  const salesChannels = useGetSalesChannelsLightList();
  const deleteStatementMutation = useDeleteStatement();

  const providersOptions = providers?.pages.flat().map((elem: Provider) => ({
    value: elem.id,
    label: elem.name,
  }));

  const getSalesChannelOptions = () =>
    salesChannels.data?.map(channel => ({
      value: channel.id,
      label: channel.name,
    })) ?? [];

  const settlementPeriodOptions = settlementPeriods.data?.flat().map(s => ({
    label: getSettlementPeriodName(s.settlementPeriodName),
    value: s.id,
  }));

  const { t, i18n } = useTranslation();
  const i18nextPrefix = 'statements';
  const navigate = useNavigate();

  const columnsVisibility = useTableColumnsVisibility(
    'Statements7',
    SORT_COLUMNS
  );

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false, true, columnsVisibility.tableId);
  const dropdownVisible = useComponentVisible<HTMLDivElement>(false);

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters =
    searchById[columnsVisibility.tableId] || statementsSearchInitialValues;

  const uploadCSVFileChunks = usePOSTChunkedUpload();

  const { data, isLoading, fetchNextPage, isFetchingNextPage, refetch } =
    useGetStatementListInfinite({
      page: 0,
      itemCount: 25,
      ...filters,
    });

  const statementStatistic = useGetStatementStatistics({
    page: 0,
    itemCount: 1,
    ...filters,
  });

  const tableData = getNodes<IStatement>(data?.pages.flat());

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }

    navigate(`/statements/${action.payload.id}/summaryRows`);
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const hasDetailPermission = hasPermission('StatementPermissionsLIST**', 16);
  const tableSelect = hasDetailPermission ? select : undefined;

  const clearPopupValues = (setFieldValue: any) => {
    setFieldValue(
      'residenceCertificate.residenceCertificate.SalesChannelId',
      undefined
    );

    setFieldValue(
      'residenceCertificate.residenceCertificate.SalesChannelName',
      undefined
    );

    setFieldValue(
      'residenceCertificate.residenceCertificate.providerIdName',
      undefined
    );

    setFieldValue(
      'residenceCertificate.residenceCertificate.SettlementPeriodId',
      undefined
    );
    setFieldValue('residenceCertificate.settlementFile', undefined);
    setFieldValue(
      'residenceCertificate.residenceCertificate.providerId',
      undefined
    );
    setFieldValue('residenceCertificate.currencyId', null);
  };

  const uploadFile = async (
    certificate: IStatementImportPayload,
    actions: PopupActions,
    setFieldValue: any,
    values: any
  ) => {
    if (certificate.File) {
      await uploadCSVFileChunks.mutateAsync(
        {
          file: certificate.File,
          salesChannelId: certificate.SalesChannelId,
          providerId: certificate.ProviderId,
          settlementPeriodId: certificate.SettlementPeriodId,
          templateType: values.residenceCertificate.templateType,
          currencyId:
            typeof certificate.CurrencyId === 'number' &&
            certificate.CurrencyId > 0
              ? certificate.CurrencyId
              : null,
          onProgress: (progress: number) => {
            setFieldValue('residenceCertificate.progress', progress);
          },
        },

        {
          onSuccess: async () => {
            await refetch();
            actions.close();
            clearPopupValues(setFieldValue);
            setFieldValue('residenceCertificate.progress', 0);
          },
        }
      );
    }
  };

  const totalValueLength = formatCurrency(
    statementStatistic?.data?.allAmount
  ).length;

  const deleteStatement = async (id: string) => {
    await deleteStatementMutation.mutateAsync(id, {
      onSuccess: async () => {
        await refetch();
        await statementStatistic.refetch();
      },
    });
  };

  const tableColumns = magellanColumns<IStatement>([
    {
      label: t('input.label.settlementPeriod'),
      renderCell: (item: IStatement) => (
        <TableItem>
          <TableItem.Text
            text={getSettlementPeriodName(item.settlementPeriodName)}
          />
        </TableItem>
      ),
      resize: true,
      hide: columnsVisibility.hide(SORT_COLUMNS.settlementPeriod),
      footer: t('table.total'),
    },
    {
      label: t('input.label.salesChannel'),
      renderCell: (item: IStatement) => (
        <TableItem>
          <TableItem.Text text={item.salesChannelName} />
        </TableItem>
      ),
      resize: true,
      hide: columnsVisibility.hide(SORT_COLUMNS.salesChannel),
    },
    {
      label: t('input.label.provider'),
      renderCell: (item: IStatement) => (
        <TableItem>
          <TableItem.Text text={item.providerName} />
        </TableItem>
      ),
      resize: true,
      hide: columnsVisibility.hide(SORT_COLUMNS.provider),
    },
    {
      label: t('input.label.totalValue'),
      renderCell: (item: IStatement) => (
        <TableItem>
          <TableItem.Currency
            text={formatCurrency(item.defaultTotalValue, {
              length: totalValueLength,
              suffix: CURRENCY_ID[item.defaultCurrency],
            })}
          />
        </TableItem>
      ),
      resize: true,
      hide: columnsVisibility.hide(SORT_COLUMNS.totalValue),
      footer: formatCurrency(statementStatistic.data?.allAmount, {
        length: totalValueLength,
      }),
    },
    {
      label: t('input.label.status'),
      renderCell: (item: IStatement) => (
        <TableItem justifyPosition='center'>
          <LabeledStatus.Statement status={item.statementStatus} />
        </TableItem>
      ),
      resize: true,
      hide: columnsVisibility.hide(SORT_COLUMNS.status),
    },
    {
      label: t('input.label.generalConsistency'),
      renderCell: (item: IStatement) => (
        <TableItem justifyPosition='center'>
          {(item.generalConsistency?.id ?? item.consistency) && (
            <LabeledStatementConsistancy
              status={item.generalConsistency?.id ?? item.consistency!}
            />
          )}
        </TableItem>
      ),
      resize: true,
      hide: columnsVisibility.hide(SORT_COLUMNS.generalConsistency),
      minWidth: 140,
    },
    {
      label: t('input.label.detailedConsistency'),
      renderCell: (item: IStatement) => (
        <TableItem justifyPosition='center'>
          {(item.detailedConsistency?.id ?? item.consistency) && (
            <LabeledStatementConsistancy
              status={item.detailedConsistency?.id ?? item.consistency!}
            />
          )}
        </TableItem>
      ),
      resize: true,
      hide: columnsVisibility.hide(SORT_COLUMNS.detailedConsistency),
      minWidth: 140,
    },
    {
      label: '',
      minWidth: 56,
      renderCell: (item: IStatement) => (
        <TableItem justifyPosition='end'>
          <TableDropdown justifyPosition='end' style={{ padding: 0 }}>
            <Popup.DeleteWarning
              name={`${getSettlementPeriodName(
                item.settlementPeriodName
              )} ${item.salesChannelName} ${item.providerName}`}
              onRemove={() => {
                deleteStatement(item.id);
              }}
              trigger={
                <Button
                  type='button'
                  variant={BUTTON_VARIANT.remove}
                  isClear
                  fullWidth
                  size='compact'
                  buttonStyle={{ justifyContent: 'start' }}>
                  <Button.Icon icon={<PiTrash />} />
                  <Button.Text text={t('button.remove')} />
                </Button>
              }
            />
          </TableDropdown>
        </TableItem>
      ),
      hide: columnsVisibility.hide(SORT_COLUMNS.status),
    },
  ]);

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          hideBackButton
          topbarContent={
            <>
              <QueryFilter.ButtonOpener
                i18nextPrefix={i18nextPrefix}
                onClick={handleComponentVisible}
                variant={BUTTON_VARIANT.default}
                isClear
              />
              <Table.CustomColumnsButton
                columns={SORT_COLUMNS}
                isClear
                visibility={columnsVisibility}
                i18nextPrefix='input.label'
                customI18next=''
              />
              <Button
                isClear
                onlyIcon
                onClick={dropdownVisible.handleComponentVisible}
                outsideChildren={
                  <DropdownMenu
                    ref={dropdownVisible.ref}
                    visible={dropdownVisible.isComponentVisible}
                    style={{ padding: 0 }}
                    justifyPosition='center'>
                    <Link
                      to={
                        i18n.language === 'pl'
                          ? `${process.env.PUBLIC_URL}/statement_template_PL.csv`
                          : `${process.env.PUBLIC_URL}/statement_template_EN.csv`
                      }
                      target='_blank'
                      download>
                      <Button
                        fullWidth
                        isClear
                        buttonStyle={{ justifyContent: 'start' }}>
                        <Button.Icon icon={<PiMicrosoftExcelLogo />} />
                        <Button.Text
                          text={t('button.download-variant', {
                            variant: t('statements.template'),
                          })}
                        />
                      </Button>
                    </Link>
                    <Link
                      to={
                        i18n.language === 'pl'
                          ? `${process.env.PUBLIC_URL}/statement_template_description_PL.csv`
                          : `${process.env.PUBLIC_URL}/statement_template_description_EN.csv`
                      }
                      target='_blank'
                      download>
                      <Button
                        fullWidth
                        isClear
                        buttonStyle={{ justifyContent: 'start' }}>
                        <Button.Icon icon={<PiMicrosoftExcelLogo />} />
                        <Button.Text
                          text={t('button.download-variant', {
                            variant: t('statements.template-description'),
                          })}
                        />
                      </Button>
                    </Link>
                  </DropdownMenu>
                }>
                <Button.Description description={t('button.download')} />
                <Button.Icon icon={<PiDownloadBold />} />
              </Button>

              {hasPermission('StatementPermissionsLIST**', 1) && (
                <Formik
                  enableReinitialize={true}
                  initialValues={{
                    residenceCertificate: {
                      providerId: '',
                      settlementFile: undefined,
                      SalesChannelId: undefined,
                      SettlementPeriodId: undefined,
                      providers: undefined,
                      progress: 0,
                      templateType: '1',
                      currencyId: null,
                      residenceCertificate: {
                        SettlementPeriodId: '',
                        providerId: '',
                        providerIdName: '',
                        SalesChannelId: '',
                        SalesChannelName: '',
                      },
                    },
                  }}
                  onSubmit={() => {}}>
                  {({
                    handleSubmit,
                    setFieldValue,
                    values,
                    ...formikHelpers
                  }) => (
                    <MagellanPopups.PopupUploadFile
                      onAdd={(
                        certificate: IStatementImportPayload,
                        actions: PopupActions
                      ) =>
                        uploadFile(certificate, actions, setFieldValue, values)
                      }
                      isUploading={uploadCSVFileChunks.isPending}
                      progressIndicator={values.residenceCertificate.progress}
                      clearPopupValues={() => clearPopupValues(setFieldValue)}
                      trigger={
                        <Button type='button'>
                          <Button.Text text={t('button.add')} />

                          <Button.Icon icon={<PiFileCsvDuotone />} />
                        </Button>
                      }
                    />
                  )}
                </Formik>
              )}
            </>
          }>
          <QueryFilter<IStatementsSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<IStatementsSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values =>
                  setSearchById(columnsVisibility.tableId, values)
                }>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='startSettlementPeriodId'
                    label={t('input.label.startSettlementPeriod')}
                    placeholder=''
                    options={settlementPeriodOptions}
                  />
                  <FormInput.Select
                    name='endSettlementPeriodId'
                    placeholder=''
                    label={t('input.label.endSettlementPeriod')}
                    options={settlementPeriodOptions}
                  />
                  <FormInput.Select
                    name='salesChannels'
                    label={t('input.label.salesChannel')}
                    options={getSalesChannelOptions()}
                  />
                  <FormInput.Select
                    name='providers'
                    label={t('input.label.provider')}
                    options={providersOptions}
                  />
                  <FormInput.Select
                    name='hasUnrelatedRows'
                    label={t('input.label.hasUnrelatedRows')}
                    options={booleanSelectOptions}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data =>
                  data.key === 'startSettlementPeriodId'
                    ? settlementPeriodOptions?.find(o => o.value === data.value)
                        ?.label ?? '...'
                    : data.key === 'endSettlementPeriodId'
                    ? settlementPeriodOptions?.find(o => o.value === data.value)
                        ?.label ?? '...'
                    : data.key === 'salesChannels'
                    ? getSalesChannelOptions()?.find(
                        o => o.value === data.value
                      )?.label ?? '...'
                    : data.key === 'providers'
                    ? providersOptions?.find(o => o.value === data.value)
                        ?.label ?? '...'
                    : data.key === 'hasUnrelatedRows'
                    ? data.value !== undefined
                      ? `${t('input.label.hasUnrelatedRows')}: ${
                          data.value === true
                            ? t('input.option.yes')
                            : t('input.option.no')
                        } `
                      : undefined
                    : `${data.value}`
                }
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <FooterTable
        select={tableSelect}
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        id={columnsVisibility.tableId}
        data={tableData}
        columns={tableColumns}
        displayMode
      />
    </TopbarPage>
  );
};

export default Statements;
